import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import PostEditor from "@/components/admin/PostEditor";
import NewsArticleBody from "@/components/news/NewsArticleBody";
import { sanitizePostHtml } from "@/lib/sanitize";
import { slugify } from "@/lib/slug";
import { toast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/useLanguage";
import {
  ArrowLeft, Save, Upload, X, Eye, Trash2, Globe, FileWarning,
  AlertCircle, ExternalLink as ExternalLinkIcon, Pencil, Send,
} from "lucide-react";
import SendCampaignDialog from "@/components/admin/SendCampaignDialog";


import type { Database } from "@/integrations/supabase/types";
import type { NewsArticle as NewsArticleShape } from "@/lib/posts";

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/** Strip protocol + leading www from a URL → "cio.se". Empty string on failure. */
const hostnameFromUrl = (url: string): string => {
  try {
    return new URL(url.trim()).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

type PostRow = Database["public"]["Tables"]["posts"]["Row"];
type Category = Database["public"]["Enums"]["post_category"];

/** Snapshot of the editable form fields, used for dirty tracking. */
interface FormSnapshot {
  title: string;
  slug: string;
  excerpt: string;
  category: Category;
  eventDate: string;
  publishedAt: string;
  coverUrl: string | null;
  authorName: string;
  bodyHtml: string;
  isExternal: boolean;
  sourceName: string;
  sourceUrl: string;
  sourceAuthor: string;
  originalPublishedAt: string;
  ctaLabel: string;
  ctaUrl: string;
}

const snapshotFromRow = (data: PostRow): FormSnapshot => ({
  title: data.title,
  slug: data.slug,
  excerpt: data.excerpt ?? "",
  category: data.category,
  eventDate: data.event_date ?? "",
  publishedAt: data.published_at ? data.published_at.slice(0, 10) : "",
  coverUrl: data.cover_image_url ?? null,
  authorName: data.author_name ?? "",
  bodyHtml: data.body_html ?? "",
  isExternal: data.is_external ?? false,
  sourceName: data.source_name ?? "",
  sourceUrl: data.source_url ?? "",
  sourceAuthor: data.source_author ?? "",
  originalPublishedAt: data.original_published_at ?? "",
  ctaLabel: (data as { cta_label?: string | null }).cta_label ?? "",
  ctaUrl: (data as { cta_url?: string | null }).cta_url ?? "",
});

const AdminPostEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { localize } = useLanguage();

  const [post, setPost] = useState<PostRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverDragOver, setCoverDragOver] = useState(false);
  const [showSlug, setShowSlug] = useState(false);

  // Form fields
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [excerpt, setExcerpt] = useState("");
  const [category, setCategory] = useState<Category>("article");
  const [eventDate, setEventDate] = useState("");
  const [publishedAt, setPublishedAt] = useState(""); // YYYY-MM-DD
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [authorName, setAuthorName] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");

  // External / repost fields
  const [isExternal, setIsExternal] = useState(false);
  const [sourceName, setSourceName] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceAuthor, setSourceAuthor] = useState("");
  const [originalPublishedAt, setOriginalPublishedAt] = useState("");

  // Optional end-of-article CTA
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");

  // Snapshot of last saved state for dirty tracking
  const [savedSnapshot, setSavedSnapshot] = useState<FormSnapshot | null>(null);

  // Pre-publish confirmation
  const [publishDialog, setPublishDialog] = useState<{
    open: boolean;
    warnings: string[];
  }>({ open: false, warnings: [] });

  // Newsletter send
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [campaigns, setCampaigns] = useState<Array<{
    id: string;
    sent_at: string;
    locale_filter: string;
    recipient_count: number;
    sent_by: string | null;
  }>>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);


  const applySnapshot = useCallback((snap: FormSnapshot) => {
    setTitle(snap.title);
    setSlug(snap.slug);
    setSlugTouched(snap.slug !== slugify(snap.title));
    setExcerpt(snap.excerpt);
    setCategory(snap.category);
    setEventDate(snap.eventDate);
    setPublishedAt(snap.publishedAt);
    setCoverUrl(snap.coverUrl);
    setAuthorName(snap.authorName);
    setBodyHtml(snap.bodyHtml);
    setIsExternal(snap.isExternal);
    setSourceName(snap.sourceName);
    setSourceUrl(snap.sourceUrl);
    setSourceAuthor(snap.sourceAuthor);
    setOriginalPublishedAt(snap.originalPublishedAt);
    setCtaLabel(snap.ctaLabel);
    setCtaUrl(snap.ctaUrl);
  }, []);

  useEffect(() => {
    if (!id) return;
    let active = true;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase.from("posts").select("*").eq("id", id).maybeSingle();
      if (!active) return;
      if (error || !data) {
        toast({ title: "Post not found", description: error?.message, variant: "destructive" });
        navigate("/admin/posts");
        return;
      }
      setPost(data as PostRow);
      const snap = snapshotFromRow(data as PostRow);
      applySnapshot(snap);
      setSavedSnapshot(snap);
      setLoading(false);
    })();

    return () => { active = false; };
  }, [id, navigate, applySnapshot]);

  const loadCampaigns = useCallback(async () => {
    if (!id) return;
    const { data } = await supabase
      .from("newsletter_campaigns")
      .select("id, sent_at, locale_filter, recipient_count, sent_by")
      .eq("post_id", id)
      .order("sent_at", { ascending: false });
    setCampaigns(data ?? []);
  }, [id]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? null));
  }, []);

  // Auto-derive slug from title until user touches the slug field
  useEffect(() => {
    if (!slugTouched && title) setSlug(slugify(title));
  }, [title, slugTouched]);


  const sanitizedPreview = useMemo(() => sanitizePostHtml(bodyHtml), [bodyHtml]);

  // Build the current form snapshot for diffing.
  const currentSnapshot: FormSnapshot = {
    title, slug, excerpt, category, eventDate, publishedAt, coverUrl,
    authorName, bodyHtml, isExternal, sourceName, sourceUrl, sourceAuthor,
    originalPublishedAt, ctaLabel, ctaUrl,
  };

  const dirty = useMemo(() => {
    if (!savedSnapshot) return false;
    const keys = Object.keys(currentSnapshot) as (keyof FormSnapshot)[];
    return keys.some((k) => currentSnapshot[k] !== savedSnapshot[k]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSnapshot, title, slug, excerpt, category, eventDate, publishedAt,
      coverUrl, authorName, bodyHtml, isExternal, sourceName, sourceUrl,
      sourceAuthor, originalPublishedAt, ctaLabel, ctaUrl]);

  // Warn before closing the browser tab with unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Note: in-app navigation blocking requires a data router; we rely on the
  // beforeunload handler above for browser-level protection.

  const validate = (): string | null => {
    if (!title.trim()) return "Title is required";
    if (!slug.trim()) return "Slug is required";
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return "Slug must be kebab-case (lowercase letters, numbers, hyphens)";
    if (category === "event" && !eventDate) return "Event date is required for events";
    if (isExternal) {
      if (!sourceUrl.trim()) return "Source URL is required for external posts";
      try { new URL(sourceUrl.trim()); } catch { return "Source URL must be a valid URL"; }
    }
    if (ctaLabel.trim()) {
      const url = ctaUrl.trim();
      if (url && !url.startsWith("/")) {
        try { new URL(url); } catch { return "CTA URL must be a valid URL or start with /"; }
      }
    }
    return null;
  };

  const validationError = validate();

  /** Soft warnings shown in the pre-publish dialog. Hard errors block. */
  const publishWarnings = (): string[] => {
    const w: string[] = [];
    if (!excerpt.trim()) w.push("Excerpt is empty — listings will fall back to the title only.");
    if (!coverUrl) w.push("No cover image — the news card and social previews will look bare.");
    if (!isExternal) {
      // Strip HTML to estimate real text length.
      const text = (bodyHtml || "").replace(/<[^>]+>/g, "").trim();
      if (text.length < 200) w.push("Body looks very short (< 200 chars). Add some content before publishing.");
    }
    return w;
  };

  const save = async (opts: { publish?: boolean; unpublish?: boolean } = {}) => {
    if (!post) return;
    const err = validate();
    if (err) {
      toast({ title: "Cannot save", description: err, variant: "destructive" });
      return;
    }
    setSaving(true);

    const clean = sanitizePostHtml(bodyHtml);

    const update: Database["public"]["Tables"]["posts"]["Update"] = {
      title: title.trim(),
      slug: slug.trim(),
      excerpt: excerpt.trim(),
      category,
      event_date: category === "event" ? eventDate || null : null,
      cover_image_url: coverUrl,
      author_name: authorName.trim() || null,
      body_html: clean,
      is_external: isExternal,
      source_name: isExternal ? (sourceName.trim() || hostnameFromUrl(sourceUrl) || null) : null,
      source_url: isExternal ? sourceUrl.trim() || null : null,
      source_author: isExternal ? sourceAuthor.trim() || null : null,
      original_published_at: isExternal ? originalPublishedAt || null : null,
      ...(ctaLabel.trim()
        ? { cta_label: ctaLabel.trim(), cta_url: ctaUrl.trim() || null }
        : { cta_label: null, cta_url: null }),
    } as Database["public"]["Tables"]["posts"]["Update"];

    if (publishedAt) {
      update.published_at = new Date(`${publishedAt}T00:00:00.000Z`).toISOString();
    }
    if (opts.publish) {
      update.status = "published";
      if (!publishedAt && !post.published_at) {
        update.published_at = new Date().toISOString();
      }
    } else if (opts.unpublish) {
      update.status = "draft";
    }

    const { data, error } = await supabase
      .from("posts")
      .update(update)
      .eq("id", post.id)
      .select("*")
      .single();
    setSaving(false);

    if (error || !data) {
      const msg = error?.code === "23505"
        ? "That slug is already in use — choose a different one."
        : error?.message ?? "Unknown error";
      toast({ title: "Save failed", description: msg, variant: "destructive" });
      return;
    }
    setPost(data as PostRow);
    const snap = snapshotFromRow(data as PostRow);
    setSavedSnapshot(snap);
    setBodyHtml(data.body_html ?? "");

    if (opts.publish) {
      const livePath = localize(`/news/${data.slug}`);
      toast({
        title: "Published",
        description: (
          <a
            href={livePath}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-primary"
          >
            View live page ↗
          </a>
        ),
      });
    } else if (opts.unpublish) {
      toast({ title: "Unpublished" });
    } else {
      toast({ title: post.status === "published" ? "Changes saved" : "Draft saved" });
    }
  };

  const attemptPublish = () => {
    const err = validate();
    if (err) {
      toast({ title: "Cannot publish", description: err, variant: "destructive" });
      return;
    }
    const warnings = publishWarnings();
    setPublishDialog({ open: true, warnings });
  };

  const uploadCoverFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Not an image", description: file.name, variant: "destructive" });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast({ title: "Image too large", description: "Max 10 MB.", variant: "destructive" });
      return;
    }
    setUploadingCover(true);
    const ext = file.name.split(".").pop() || "jpg";
    const path = `cover/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage
      .from("post-covers")
      .upload(path, file, { cacheControl: "3600", upsert: false });
    if (error) {
      setUploadingCover(false);
      toast({ title: "Cover upload failed", description: error.message, variant: "destructive" });
      return;
    }
    const { data, error: signErr } = await supabase.storage
      .from("post-covers")
      .createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
    if (signErr || !data) {
      setUploadingCover(false);
      toast({ title: "Could not sign URL", description: signErr?.message, variant: "destructive" });
      return;
    }
    setCoverUrl(data.signedUrl);
    setUploadingCover(false);
  };

  const onPickCover = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) await uploadCoverFile(file);
  };

  const onCoverDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setCoverDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await uploadCoverFile(file);
  };

  const deletePost = async () => {
    if (!post) return;
    const { error } = await supabase.from("posts").delete().eq("id", post.id);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    setSavedSnapshot(null); // suppress dirty guard on the way out
    toast({ title: "Post deleted" });
    navigate("/admin/posts");
  };

  if (loading || !post) {
    return <div className="text-muted-foreground text-sm">Loading…</div>;
  }

  const isPublished = post.status === "published";
  const slugChangedFromPublished = isPublished && savedSnapshot && slug !== savedSnapshot.slug;
  const livePath = localize(`/news/${post.slug}`);

  // Build a NewsArticle shape for the inline preview from current form state.
  const previewArticle: NewsArticleShape = {
    id: post.id,
    slug: slug || post.slug,
    title,
    excerpt,
    date: new Date(
      isExternal && originalPublishedAt
        ? originalPublishedAt
        : publishedAt || post.published_at || post.created_at
    ),
    category,
    href: localize(`/news/${slug || post.slug}`),
    isExternal,
    image: coverUrl ?? undefined,
    author: (isExternal ? sourceAuthor : authorName) || undefined,
    bodyHtml: sanitizedPreview,
    coverImageUrl: coverUrl,
    updatedAt: post.updated_at,
    externalUrl: isExternal ? (sourceUrl || null) : null,
  };

  return (
    <TooltipProvider delayDuration={150}>
      <Helmet>
        <title>{title || "Edit post"} · Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* In-app unsaved-changes guard removed: requires a data router.
          Browser-level protection is handled by the beforeunload listener. */}

      {/* Pre-publish confirm */}
      <Dialog
        open={publishDialog.open}
        onOpenChange={(open) => setPublishDialog((d) => ({ ...d, open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish this post?</DialogTitle>
            <DialogDescription>
              It will become visible at <code className="text-foreground">{livePath}</code> and
              eligible for search engines.
            </DialogDescription>
          </DialogHeader>
          {publishDialog.warnings.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm space-y-2">
              <div className="flex items-center gap-2 font-medium text-amber-200">
                <AlertCircle className="h-4 w-4" /> Heads up
              </div>
              <ul className="list-disc pl-5 space-y-1 text-foreground/90">
                {publishDialog.warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPublishDialog({ open: false, warnings: [] })}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                setPublishDialog({ open: false, warnings: [] });
                await save({ publish: true });
              }}
              disabled={saving}
            >
              <Globe className="w-4 h-4 mr-2" /> Publish now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mb-4">
        <Link
          to="/admin/posts"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> All posts
        </Link>
      </div>

      <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 mb-6 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-semibold tracking-tight truncate">
                {isPublished ? "Edit post" : "Edit draft"}
              </h1>
              {dirty && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 text-amber-200 px-2.5 py-0.5 text-xs font-medium">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                  Unsaved changes
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isPublished && post.published_at
                ? `Published ${new Date(post.published_at).toLocaleDateString()}`
                : "Not published yet"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isPublished && (
              <Button asChild variant="ghost" size="sm">
                <a href={livePath} target="_blank" rel="noopener noreferrer">
                  <ExternalLinkIcon className="w-4 h-4 mr-2" /> View live
                </a>
              </Button>
            )}
            {isPublished && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSendDialogOpen(true)}
                disabled={dirty}
                title={dirty ? "Save changes before sending" : undefined}
              >
                <Send className="w-4 h-4 mr-2" /> Send to subscribers
              </Button>
            )}

            <div className="inline-flex rounded-md border border-border overflow-hidden">
              <Button
                variant="ghost"
                size="sm"
                className="rounded-none"
                onClick={() => setShowPreview((s) => !s)}
              >
                <Eye className="w-4 h-4 mr-2" />
                {showPreview ? "Hide preview" : "Preview"}
              </Button>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="rounded-none border-l border-border px-2"
                title="Open full preview in a new tab"
              >
                <Link to={`/admin/posts/${post.id}/preview`} target="_blank" rel="noopener noreferrer">
                  <ExternalLinkIcon className="w-4 h-4" />
                </Link>
              </Button>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => save()}
                    disabled={saving || !dirty || !!validationError}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isPublished ? "Save changes" : "Save draft"}
                  </Button>
                </span>
              </TooltipTrigger>
              {!dirty && !validationError && (
                <TooltipContent>No changes to save</TooltipContent>
              )}
              {validationError && <TooltipContent>{validationError}</TooltipContent>}
            </Tooltip>
            {isPublished ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={saving}>
                    <FileWarning className="w-4 h-4 mr-2" /> Unpublish
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Unpublish this post?</AlertDialogTitle>
                    <AlertDialogDescription>
                      The post will be removed from the public site and search index until you
                      publish it again. Your content is preserved as a draft.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => save({ unpublish: true })}>
                      Unpublish
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button size="sm" onClick={attemptPublish} disabled={saving || !!validationError}>
                      <Globe className="w-4 h-4 mr-2" /> Publish
                    </Button>
                  </span>
                </TooltipTrigger>
                {validationError && <TooltipContent>{validationError}</TooltipContent>}
              </Tooltip>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Delete post">
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this post?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This cannot be undone. The post and its slug will be permanently removed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deletePost}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>


      <div className={`grid gap-6 ${showPreview ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}>
        <div className="space-y-8">
          {/* ── Basics ─────────────────────────────────────── */}
          <section className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Post title"
              />
              {/* URL row — collapsed by default */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                <span className="shrink-0">URL:</span>
                <code className="truncate text-foreground/80">/news/{slug || "…"}</code>
                <button
                  type="button"
                  onClick={() => setShowSlug((s) => !s)}
                  className="inline-flex items-center gap-1 text-primary hover:underline shrink-0"
                >
                  <Pencil className="h-3 w-3" />
                  {showSlug ? "Hide" : "Edit"}
                </button>
              </div>
              {showSlug && (
                <div className="space-y-1 pt-1">
                  <Input
                    id="slug"
                    value={slug}
                    onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); }}
                    placeholder="post-url-slug"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground">
                      Auto-generated from the title. Lowercase, hyphens only.
                    </p>
                    {slugTouched && title && slug !== slugify(title) && (
                      <button
                        type="button"
                        onClick={() => { setSlug(slugify(title)); setSlugTouched(false); }}
                        className="text-xs text-primary hover:underline shrink-0"
                      >
                        Regenerate from title
                      </button>
                    )}
                  </div>
                  {slugChangedFromPublished && (
                    <div className="flex items-start gap-1.5 text-xs text-amber-300 mt-1">
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>
                        Changing the slug on a published post breaks the existing URL — old links will 404.
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select value={category} onValueChange={(v) => setCategory(v as Category)}>
                  <SelectTrigger id="category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="article">Article</SelectItem>
                    <SelectItem value="insight">Insight</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="case_study">Case study</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="source_type">Source</Label>
                <Select
                  value={isExternal ? "external" : "original"}
                  onValueChange={(v) => setIsExternal(v === "external")}
                >
                  <SelectTrigger id="source_type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="original">Original</SelectItem>
                    <SelectItem value="external">External / repost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isExternal && (
              <div className="space-y-2">
                <Label htmlFor="source_url">Source URL *</Label>
                <Input
                  id="source_url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://…"
                />
                <p className="text-xs text-muted-foreground">
                  External posts render as a short stub linking to this URL.
                </p>
              </div>
            )}

            {category === "event" && (
              <div className="space-y-2">
                <Label htmlFor="event_date">Event date</Label>
                <Input
                  id="event_date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                />
              </div>
            )}
          </section>

          {/* ── Summary & cover ────────────────────────────── */}
          <section className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="excerpt">Excerpt</Label>
              <Textarea
                id="excerpt"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="Short summary used in listings and meta description."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Optional. If empty, the news card shows the title only. {excerpt.length} chars (recommended &lt; 160).
              </p>
            </div>

            <div className="space-y-2">
              <Label>Cover image</Label>
              <div
                onDragOver={(e) => { e.preventDefault(); setCoverDragOver(true); }}
                onDragLeave={() => setCoverDragOver(false)}
                onDrop={onCoverDrop}
                className={`rounded-md border-2 border-dashed transition-colors ${
                  coverDragOver ? "border-primary bg-primary/5" : "border-border bg-muted/20"
                } ${coverUrl ? "p-3" : "p-6"}`}
              >
                {coverUrl ? (
                  <div className="relative inline-block">
                    <img
                      src={coverUrl}
                      alt="Cover preview"
                      className="max-h-48 rounded-md border border-border"
                    />
                    <button
                      type="button"
                      aria-label="Remove cover"
                      onClick={() => setCoverUrl(null)}
                      className="absolute -top-2 -right-2 bg-background border border-border rounded-full p-1 hover:bg-muted"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-center">
                    <Upload className="w-5 h-5 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {coverDragOver ? "Drop image here" : "Drag & drop an image, or"}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploadingCover}
                    >
                      {uploadingCover ? "Uploading…" : "Choose file"}
                    </Button>
                    <p className="text-xs text-muted-foreground">PNG/JPG/WebP · max 10 MB</p>
                  </div>
                )}
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={onPickCover}
                />
              </div>
            </div>
          </section>

          {/* ── Meta ──────────────────────────────────────── */}
          <section className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="author">Author</Label>
              <Input
                id="author"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                placeholder="Author name (optional)"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="published_at">Publish date</Label>
              <Input
                id="published_at"
                type="date"
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to use today on publish. Backdate to slot a post earlier in the feed.
              </p>
            </div>
          </section>

          {/* ── Body ──────────────────────────────────────── */}
          <section className="space-y-2">
            <Label>Body {isExternal && <span className="text-muted-foreground font-normal">(optional intro)</span>}</Label>
            <PostEditor value={bodyHtml} onChange={setBodyHtml} />
            <p className="text-xs text-muted-foreground">
              HTML is sanitized on save — only basic formatting, links and images are preserved.
            </p>
          </section>

          {/* ── Call to action ────────────────────────────── */}
          <section className="space-y-3 rounded-md border border-border/60 bg-muted/20 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-sm">Call to action</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Renders as a button at the end of the article.
                </p>
              </div>
              <Switch
                checked={!!ctaLabel || !!ctaUrl}
                onCheckedChange={(on) => {
                  if (on) {
                    if (!ctaLabel) setCtaLabel("Get in touch");
                  } else {
                    setCtaLabel("");
                    setCtaUrl("");
                  }
                }}
                aria-label="Toggle call to action"
              />
            </div>
            {(!!ctaLabel || !!ctaUrl) && (
              <div className="grid sm:grid-cols-2 gap-4 pt-1">
                <div className="space-y-2">
                  <Label htmlFor="cta_label" className="text-xs">Button label</Label>
                  <Input
                    id="cta_label"
                    value={ctaLabel}
                    onChange={(e) => setCtaLabel(e.target.value)}
                    placeholder="e.g. Get in touch"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cta_url" className="text-xs">Button URL</Label>
                  <Input
                    id="cta_url"
                    value={ctaUrl}
                    onChange={(e) => setCtaUrl(e.target.value)}
                    placeholder="/contact (default) or https://…"
                  />
                </div>
              </div>
            )}
          </section>
        </div>


        {showPreview && (
          <aside className="lg:sticky lg:top-6 self-start">
            <div className="rounded-lg border border-border bg-card overflow-hidden">
              <div className="px-4 py-2 border-b border-border text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 flex items-center justify-between">
                <span>Preview — matches the live page</span>
                <Link
                  to={`/admin/posts/${post.id}/preview`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline normal-case tracking-normal"
                >
                  Open full preview <ExternalLinkIcon className="h-3 w-3" />
                </Link>
              </div>
              <div className="bg-background">
                <NewsArticleBody article={previewArticle} showBackLinks={false} />
              </div>
            </div>
          </aside>
        )}
      </div>

      {isPublished && (
        <section className="mt-10 pt-6 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Newsletter sends</h2>
            {campaigns.length > 0 && (
              <Link
                to="/admin/email"
                className="text-xs text-primary hover:underline"
              >
                View email log →
              </Link>
            )}
          </div>
          {campaigns.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              This post hasn't been sent to subscribers yet.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {campaigns.map((c) => {
                const sentByLabel =
                  c.sent_by && currentUserId && c.sent_by === currentUserId
                    ? "you"
                    : c.sent_by
                    ? `admin ${c.sent_by.slice(0, 8)}`
                    : "—";
                return (
                  <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                    <div className="min-w-0">
                      <span className="text-foreground">
                        {new Date(c.sent_at).toLocaleString()}
                      </span>
                      <span className="text-muted-foreground">
                        {" "}· {c.locale_filter === "all" ? "All locales" : c.locale_filter.toUpperCase()}
                        {" "}· by {sentByLabel}
                      </span>
                    </div>
                    <span className="text-muted-foreground whitespace-nowrap">
                      {c.recipient_count} recipient{c.recipient_count === 1 ? "" : "s"}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      <SendCampaignDialog
        open={sendDialogOpen}
        onOpenChange={setSendDialogOpen}
        postId={post.id}
        postTitle={title}
        dirty={dirty}
        onSent={loadCampaigns}
      />
    </TooltipProvider>

  );
};

export default AdminPostEdit;
